import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtTokenService } from '../../core/shared/jwt-token.service';
import { AlertService } from '../../shared/alert/shared/alert.service';
import { AlertTypes } from '../../shared/alert/shared/alert-types';
import { LoginData } from '../shared/interfaces/login-data.interface';
import { UserDataResponse } from '../shared/interfaces/login-data-response.interface';
import {
  CompleteRequiredTwoFactorSetupRequest,
  EnableAuthenticatorResponse,
  LoginResponse,
  TwoFactorChallengeResponse,
  TwoFactorSetupChallengeResponse,
  TwoFactorVerifyRequest
} from '../shared/interfaces/two-factor.interface';
import { UserDataService } from '../shared/services/user-data.service';
import { LoginService } from '../shared/services/login.service';
import QRCode from 'qrcode';

@Component({
  selector: 'avs-login-overview',
  templateUrl: './login-overview.component.html',
  styleUrls: ['./login-overview.component.css']
})
export class LoginOverviewComponent {
  public loginData: LoginData = { email: '', password: '' };
  public isLoading: boolean = false;
  public loginStep: 'credentials' | 'two-factor' | 'two-factor-setup' = 'credentials';
  public twoFactorCode: string = '';
  public twoFactorChallenge?: TwoFactorChallengeResponse;
  public twoFactorSetupChallenge?: TwoFactorSetupChallengeResponse;
  public authenticatorSetup?: EnableAuthenticatorResponse;
  public authenticatorQrCodeDataUrl: string | null = null;
  public twoFactorSetupCode: string = '';
  private redirectUrl: string = '/';

  constructor(
    private loginService: LoginService,
    private router: Router,
    private route: ActivatedRoute,
    private jwtTokenService: JwtTokenService,
    private alertService: AlertService,
    private readonly userDataService: UserDataService
  ) {
    this.redirectUrl = this.route.snapshot.queryParamMap.get('redirect') || '/';

    if (loginService.isLogInValid()) {
      this.loginService.getUserViaJwtToken().subscribe((userData: UserDataResponse) => {
        console.log("Managed to retrieve user data from token");
        this.router.navigate(['/']);
      });
      return;
    }

    // A pending 2FA step handed over by the reset-password page (the password was just set there).
    const handedOver = this.router.getCurrentNavigation()?.extras.state?.['loginResponse'] as LoginResponse | undefined;
    if (handedOver && this.loginService.isTwoFactorChallenge(handedOver))
      this.openTwoFactorStep(handedOver);
    else if (handedOver && this.loginService.isTwoFactorSetupChallenge(handedOver))
      this.openTwoFactorSetupStep(handedOver);
  }

    public onLoginButtonClicked(): void {
    this.isLoading = true;
    this.loginService.login(this.loginData).subscribe({
      next: (response: LoginResponse) => {
        this.isLoading = false;

        if (this.loginService.isTwoFactorChallenge(response)) {
          this.openTwoFactorStep(response);
          return;
        }

        if (this.loginService.isTwoFactorSetupChallenge(response)) {
          this.openTwoFactorSetupStep(response);
          return;
        }

        if (this.loginService.isAuthenticatedLoginResponse(response)) {
          this.handleSuccessfulLogin(response);
          return;
        }

        this.alertService.alert('Unbekannter Login-Status.', AlertTypes.error);
      },
      error: (errorContents) => {
        this.isLoading = false;
        if (errorContents.status == 429)
          this.alertService.alert('Ihr Konto wurde vorübergehend gesperrt.', AlertTypes.error);
        else
          this.alertService.alert('Die Anmeldedaten sind ungültig.', AlertTypes.error);
      }
    });
  }

  public onTwoFactorButtonClicked(): void {
    if (!this.twoFactorChallenge || !this.twoFactorCode.trim()) {
      this.alertService.alert('Bitte geben Sie den 2FA-Code ein.', AlertTypes.error);
      return;
    }

    this.isLoading = true;

    const verifyRequest: TwoFactorVerifyRequest = {
      twoFactorToken: this.twoFactorChallenge.twoFactorToken,
      code: this.twoFactorCode.trim()
    };

    this.loginService.verifyTwoFactor(verifyRequest).subscribe({
      next: (userData: UserDataResponse) => {
        this.isLoading = false;
        this.handleSuccessfulLogin(userData);
      },
      error: (errorContents) => {
        this.isLoading = false;
        if (errorContents.status == 429) {
          this.alertService.alert('Ihr Konto wurde vorübergehend gesperrt.', AlertTypes.error);
          this.loginData.email = '';
          this.loginData.password = '';
          this.loginStep ='credentials';
        }
        else
          this.alertService.alert('Der 2FA-Code ist ungültig.', AlertTypes.error);
      }
    });
  }

  public onCompleteRequiredTwoFactorSetupClicked(): void {
    if (!this.twoFactorSetupChallenge || !this.authenticatorSetup) {
      this.alertService.alert('Die 2FA-Einrichtung konnte nicht geladen werden.', AlertTypes.error);
      return;
    }

    if (!this.twoFactorSetupCode.trim()) {
      this.alertService.alert('Bitte geben Sie den Code aus Ihrer Authenticator-App ein.', AlertTypes.error);
      return;
    }

    this.isLoading = true;

    const request: CompleteRequiredTwoFactorSetupRequest = {
      twoFactorSetupToken: this.twoFactorSetupChallenge.twoFactorSetupToken,
      secretKey: this.authenticatorSetup.secretKey,
      authenticatorUri: this.authenticatorSetup.authenticatorUri,
      code: this.twoFactorSetupCode.trim()
    };

    this.loginService.completeRequiredTwoFactorSetup(request).subscribe({
      next: (userData) => {
        this.isLoading = false;
        this.handleSuccessfulLogin(userData);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.alert('Der eingegebene Code ist ungültig oder die Einrichtung ist abgelaufen. Melden Sie sich bitte erneut an.', AlertTypes.error);
      }
    });
  }

  public onBackToLoginClicked(): void {
    this.loginStep = 'credentials';
    this.twoFactorCode = '';
    this.twoFactorSetupCode = '';
    this.twoFactorChallenge = undefined;
    this.twoFactorSetupChallenge = undefined;
    this.authenticatorSetup = undefined;
    this.authenticatorQrCodeDataUrl = null;
  }

  public onPasswordForgotClicked(): void {
    const email = this.loginData.email.trim();
    if (!email) {
      this.alertService.alert('Bitte geben Sie Ihre E-Mail-Adresse ein.', AlertTypes.error);
      return;
    }

    this.isLoading = true;
    this.loginService.forgotPassword(email).subscribe({
      next: () => {
        this.isLoading = false;
        this.alertService.alert(
          'Falls die E-Mail-Adresse registriert ist, erhalten Sie in Kürze eine E-Mail mit einem Link zum Zurücksetzen des Passworts. Der Link ist einen Tag gültig.',
          AlertTypes.success, 10000);
      },
      error: () => {
        this.isLoading = false;
        this.alertService.alert('Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.', AlertTypes.error);
      }
    });
  }
  public getStepTitle(): string {
    switch (this.loginStep) {
      case 'two-factor':
        return 'Zwei-Faktor-Bestätigung';
      case 'two-factor-setup':
        return '2FA einrichten';
      default:
        return 'Anmelden';
    }
  }

  public getStepDescription(): string {
    switch (this.loginStep) {
      case 'two-factor':
        return 'Geben Sie den Code aus Ihrer Authenticator-App ein.';
      case 'two-factor-setup':
        return 'Für dieses Konto ist 2FA verpflichtend. Richten Sie Ihre Authenticator-App jetzt ein, um fortzufahren.';
      default:
        return 'Melden Sie sich mit E-Mail und Passwort an.';
    }
  }

  public async onCopySecretKeyClicked(): Promise<void> {
    if (!this.authenticatorSetup?.secretKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.authenticatorSetup.secretKey);
      this.alertService.alert('Der Secret Key wurde in die Zwischenablage kopiert.', AlertTypes.success);
    } catch {
      this.alertService.alert('Der Secret Key konnte nicht kopiert werden.', AlertTypes.error);
    }
  }

  private openTwoFactorStep(challenge: TwoFactorChallengeResponse): void {
    this.loginStep = 'two-factor';
    this.twoFactorChallenge = challenge;
    this.twoFactorCode = '';
    this.alertService.alert('Bitte bestätigen Sie den Login mit Ihrem 2FA-Code.', AlertTypes.info);
  }

  private openTwoFactorSetupStep(challenge: TwoFactorSetupChallengeResponse): void {
    this.loginStep = 'two-factor-setup';
    this.twoFactorSetupChallenge = challenge;
    this.twoFactorSetupCode = '';

    if (challenge.secretKey && challenge.authenticatorUri) {
      const setup: EnableAuthenticatorResponse = {
        email: challenge.email ?? this.loginData.email,
        secretKey: challenge.secretKey,
        authenticatorUri: challenge.authenticatorUri,
        code: '',
        twoFactorSetupToken: challenge.twoFactorSetupToken
      };

      this.applyTwoFactorSetup(setup, true);
      return;
    }

    this.isLoading = true;
    this.loginService.getRequiredTwoFactorSetup(challenge.twoFactorSetupToken).subscribe({
      next: (setup) => this.applyTwoFactorSetup(setup, false),
      error: () => {
        this.isLoading = false;
        this.authenticatorSetup = undefined;
        this.authenticatorQrCodeDataUrl = null;
        this.alertService.alert('Die 2FA-Einrichtung konnte nicht geladen werden.', AlertTypes.error);
      }
    });
  }

  private applyTwoFactorSetup(setup: EnableAuthenticatorResponse, showRequiredHint: boolean): void {
    this.authenticatorSetup = setup;
    this.isLoading = true;

    this.buildQrCodeDataUrl(setup.authenticatorUri)
      .then((dataUrl) => {
        this.authenticatorQrCodeDataUrl = dataUrl;
        this.alertService.alert(
          showRequiredHint
            ? '2FA ist für dieses Konto verpflichtend. Scannen Sie den QR-Code und bestätigen Sie die Einrichtung.'
            : 'Die 2FA-Einrichtung wurde geladen. Scannen Sie den QR-Code und bestätigen Sie die Einrichtung.',
          AlertTypes.info
        );
      })
      .catch(() => {
        this.authenticatorQrCodeDataUrl = null;
        this.alertService.alert('Die 2FA-Einrichtung wurde geladen. Verwenden Sie bei Bedarf den Secret Key für die manuelle Einrichtung.', AlertTypes.info);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private handleSuccessfulLogin(userData: UserDataResponse): void {
    this.jwtTokenService.setJwtToken(userData.jwtToken);
    this.userDataService.updateUserData(userData);
    this.alertService.alert('Erfolgreich angemeldet', AlertTypes.success);

    if (userData.acceptTerms === false) {
      this.router.navigate(['/privacy'], {
        queryParams: { redirect: this.redirectUrl }
      });
      return;
    }

    this.router.navigateByUrl(this.redirectUrl);
  }

  private buildQrCodeDataUrl(authenticatorUri: string): Promise<string> {
    if (authenticatorUri.startsWith('data:image')) {
      return Promise.resolve(authenticatorUri);
    }

    return QRCode.toDataURL(authenticatorUri, {
      width: 220,
      margin: 1,
      errorCorrectionLevel: 'M'
    });
  }
}
